<script>
export default {
    page() {
        const title = 'Onboarding'
        const description = 'Onboarding'
        return {
            title,
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: description },

                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: description },

                { property: 'twitter:title', vmid: 'twitter:title', content: title },
                { property: 'twitter:description', vmid: 'twitter:description', content: description },
            ],
        }
    },
}
</script>

<script setup>
import { onMounted, ref } from 'vue'
import { useStore } from '@composables/use-store'
import DataLayer from '@services/data-layer/index'
import GenresPicker from './components/genres-picker'
import ArtistsPicker from './components/artists-picker'
import { uniqueFromCombinedArrays } from '@utils/arrays'
import { debounce } from './../../../utils/performance'
import { useAnalytics } from '@composables/use-analytics'

const ARTISTS_LIMIT_PER_GENRE = 5
const DEBOUNCE_DELAY = 300

const store = useStore()
const analyticsService = useAnalytics()

const currentlyPicking = ref('genres')
const genres = ref([])
const artists = ref([])
const selectedGenres = ref([])
const selectedArtists = ref([])
const isLoading = ref(false)

async function getGenres() {
    isLoading.value = true
    const res = await DataLayer.genres.getAll()
    genres.value = res.body || []
    isLoading.value = false
}

async function getArtistsByGenres() {
    isLoading.value = true
    const calls = selectedGenres.value.map(genre =>
        DataLayer.artists.getRandomByGenre(genre.name, ARTISTS_LIMIT_PER_GENRE)
    )
    const responses = await Promise.all(calls)
    artists.value = uniqueFromCombinedArrays(responses.map(res => res.body || []), 'id')
    isLoading.value = false
}

async function getArtistsByName(query) {
    if (!query) {
        getArtistsByGenres()
        return
    }
    isLoading.value = true
    const res = await DataLayer.artists.getByName(query)
    artists.value = res.body || []
    isLoading.value = false
}

async function goToArtistsPicking() {
    currentlyPicking.value = 'artists'
    if (artists.value.length > 0) return
    await getArtistsByGenres()
}

function goToGenresPicking() {
    currentlyPicking.value = 'genres'
}

function requestAllArtistsFollow() {
    return Promise.all(
        selectedArtists.value.map(artist =>
            store.dispatch('favorites/add', { type: 'artists', id: artist.id })
        )
    )
}

function requestAllGenresFollow() {
    return Promise.all(
        selectedGenres.value.map(genre =>
            store.dispatch('favorites/add', { type: 'genres', id: genre.id })
        )
    )
}

// allSettled, not all: one failed follow must not strand the member in onboarding.
async function completePicking() {
    await Promise.allSettled([requestAllArtistsFollow(), requestAllGenresFollow()])
    analyticsService.onboardingGenresFollowedCount(selectedGenres.value.length)
    window.location.href = '/'
}

function onGenrePick(genre) {
    if (selectedGenres.value.find(item => item.id === genre.id)) {
        selectedGenres.value = selectedGenres.value.filter(item => item.id !== genre.id)
    } else {
        selectedGenres.value.push({ ...genre })
    }

    // The artist suggestions are derived from the genres, so they are no longer valid.
    artists.value = []
}

function onArtistPick(artist) {
    if (selectedArtists.value.find(item => item.id === artist.id)) {
        selectedArtists.value = selectedArtists.value.filter(item => item.id !== artist.id)
    } else {
        selectedArtists.value.push({ ...artist })
    }
}

const onSearchDebounce = debounce(query => getArtistsByName(query), DEBOUNCE_DELAY)

onMounted(getGenres)
</script>

<template>
    <GenresPicker
        v-if="currentlyPicking === 'genres'"
        :genres="genres"
        :selected-genres="selectedGenres"
        :is-loading="isLoading"
        @onPick="onGenrePick"
        @onNext="goToArtistsPicking"
    />
    <ArtistsPicker
        v-else-if="currentlyPicking === 'artists'"
        :artists="artists"
        :selected-artists="selectedArtists"
        :is-loading="isLoading"
        @onPick="onArtistPick"
        @onBack="goToGenresPicking"
        @onNext="completePicking"
        @onSearch="onSearchDebounce"
    />
</template>

<style lang="scss" scoped>
@use '@design' as *;
@use 'sass:color';

</style>
